/** The ID of the Jupyter notebook DOM node containing cells. */
const NOTEBOOK_CONTAINER_ID = "notebook-container";

/** The ID of the DOM node that our custom React tree renders into. */
const KAGGLE_ROOT_ID = "kaggle-root";

const KAGGLE_FRAME_ID = "kaggle-frame";

const JUPYTER_DEV_TOOLS_ID = "jupyter-dev-tools";

/**
 * Gets or creates a DOM element if it does not exist.
 *
 * @remarks
 * Use this for creating DOM elements for React root nodes or portals.
 */
const getOrCreateElement = <K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  id: string,
  onCreate: (element: HTMLElement) => void
) => {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement(tagName);
    element.id = id;
    if (onCreate) {
      onCreate(element);
    }
  }
  return element;
};

/** Get the notebook container DOM node or throw if DNE. */
export const getNotebookContainerNode = () => {
  const notebookContainer = document.getElementById(NOTEBOOK_CONTAINER_ID)!;
  if (!notebookContainer) {
    throw new Error(
      `No notebook container with id ${NOTEBOOK_CONTAINER_ID} found.`
    );
  }
  return notebookContainer;
};

/** Get the notebook container DOM node or throw if DNE. */
export const getJupyterLabContainerNode = () =>
  getOrCreateElement("div", NOTEBOOK_CONTAINER_ID, element =>
    document.body.appendChild(element)
  );

/** Get or add a DOM node to render our custom React tree into. */
export const getOrCreateKaggleRootNode = () =>
  getOrCreateElement("div", KAGGLE_ROOT_ID, element => {
    document.body.appendChild(element);
  });

/** Get or add a DOM node to render Jupyter frame into (portal). */
export const getOrCreateKaggleFrameNode = () =>
  getOrCreateElement("div", KAGGLE_FRAME_ID, element => {
    element.setAttribute(
      "style",
      "position: fixed; display: grid; top: 0; left: 0; z-index: 0;"
    );
    document.body.appendChild(element);
  });

/** Creates a node in the Jupyter frame to render the JupyterDevTools portal in. */
export const getOrCreateJupyterDevToolsNode = () =>
  getOrCreateElement("div", JUPYTER_DEV_TOOLS_ID, element => {
    element.setAttribute("style", "width: 100%;");
    document.body.appendChild(element);
  });
